import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Navbar from "./components/Navbar";
import StickyLeadBar from "./components/StickyLeadBar";
import Hero from "./components/sections/Hero";

// Below-the-fold sections are bundled into one lazy chunk so the homepage's
// initial JS (Navbar + Hero) stays small and hydrates fast.
const BelowFold = lazy(() => import("./components/sections/BelowFold"));

// Route pages are code-split: each loads as its own chunk on navigation, so
// the homepage bundle stays small (heavy pages like Analytics pull recharts
// only when visited). The homepage (Landing) + its sections above stay eager
// for fast first paint / LCP. Crawlers are unaffected — they read the static
// prerendered HTML, not these chunks.
const CaseStudyPage = lazy(() => import("./pages/CaseStudyPage"));
const CaseStudiesIndexPage = lazy(() => import("./pages/CaseStudiesIndexPage"));
const BookDemoPage = lazy(() => import("./pages/BookDemoPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const SitePage = lazy(() => import("./pages/SitePage"));
const PlatformPage = lazy(() => import("./pages/PlatformPage"));
const IndustriesIndexPage = lazy(() => import("./pages/IndustriesIndexPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const WhatsAppAutomationPage = lazy(() => import("./pages/WhatsAppAutomationPage"));
const ChatAgentsPage = lazy(() => import("./pages/ChatAgentsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));

const Landing = () => (
  <main data-testid="landing-page" className="overflow-x-hidden bg-white">
    <Navbar />
    <Hero />
    {/* Below-fold sections load as a separate lazy chunk whose download starts
        immediately on mount (in parallel with the hero), so the page becomes
        scrollable quickly. Keeping it a separate chunk means main.js stays small
        (~194KB) and the hero/LCP isn't blocked. Own Suspense (fallback={null})
        so only this area waits — the hero stays visible while it streams in. */}
    <Suspense fallback={null}>
      <BelowFold />
    </Suspense>
  </main>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// /industries/:slug → /case-studies/:slug
const IndustryRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/case-studies/${slug}`} replace />;
};

function App() {
  return (
    <div className="App">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0b0a18",
            color: "#fff",
            border: "1px solid rgba(125,104,194,0.4)",
          },
        }}
      />
      <BrowserRouter>
        <ScrollToTop />
        <StickyLeadBar />
        <Suspense fallback={<div style={{ minHeight: "100vh", background: "#fafafb" }} />}>
        <Routes>
          <Route path="/" element={<Landing />} />

          {/* Platform */}
          <Route path="/platform" element={<PlatformPage />} />
          <Route
            path="/platform/voice-agents"
            element={<SitePage contentKey="platform-voice-agents" />}
          />
          <Route
            path="/platform/whatsapp-automation"
            element={<WhatsAppAutomationPage />}
          />
          <Route path="/platform/chat-agents" element={<ChatAgentsPage />} />
          <Route path="/platform/analytics" element={<AnalyticsPage />} />
          <Route
            path="/platform/human-handoff"
            element={<SitePage contentKey="platform-human-handoff" />}
          />

          {/* Industries */}
          <Route path="/industries" element={<IndustriesIndexPage />} />
          <Route path="/industries/:slug" element={<IndustryRedirect />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Case studies */}
          <Route path="/case-studies" element={<CaseStudiesIndexPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyPage />} />

          {/* Marketing pages */}
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/book-demo" element={<BookDemoPage />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Info pages via SitePage template */}
          <Route path="/about" element={<SitePage contentKey="about" />} />
          <Route path="/contact" element={<SitePage contentKey="contact" />} />
          <Route path="/careers" element={<SitePage contentKey="careers" />} />
          <Route path="/privacy" element={<SitePage contentKey="privacy" />} />
          <Route path="/terms" element={<SitePage contentKey="terms" />} />
          <Route path="/faqs" element={<SitePage contentKey="faqs" />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
